import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, Mail, Menu, X, ShieldAlert, ChevronDown, ChevronUp } from 'lucide-react';
import { companyInfo, services } from '../data';

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileYdelserOpen, setIsMobileYdelserOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 40) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (id) => {
    setIsMobileMenuOpen(false);
    if (location.pathname === '/') {
      const element = document.getElementById(id);
      if (element) {
        const offset = 120; // height of header
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <header className="w-full z-40 transition-all duration-300 fixed top-0 left-0 right-0 bg-transparent">
      {/* Utility Bar */}
      <div className={`w-full py-2.5 px-4 sm:px-6 lg:px-8 text-xs transition-all duration-300 ${
        isScrolled 
          ? 'hidden' 
          : 'hidden sm:block bg-transparent text-white/90 border-b border-white/10'
      }`}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-end items-center gap-2">
          <div className="flex items-center gap-6">
            <a
              href={`tel:${companyInfo.phoneFormatted}`}
              className="flex items-center gap-1.5 hover:text-sky-500 transition-colors"
            >
              <Phone className={`w-3.5 h-3.5 transition-colors ${isScrolled ? 'text-slate-400' : 'text-white/70'}`} />
              <span>Tlf: {companyInfo.phone}</span>
            </a>
            <a
              href={`mailto:${companyInfo.email}`}
              className="flex items-center gap-1.5 hover:text-sky-500 transition-colors"
            >
              <Mail className={`w-3.5 h-3.5 transition-colors ${isScrolled ? 'text-slate-400' : 'text-white/70'}`} />
              <span>{companyInfo.email}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div
        className="w-full transition-all duration-300 px-4 sm:px-6 lg:px-8 py-4 bg-white border-b border-slate-100 shadow-md"
      >
        <div className="max-w-7xl mx-auto flex flex-row md:flex-col lg:flex-row justify-between items-center md:items-start lg:items-center gap-4 lg:gap-0">
          {/* Logo */}
          <button
            onClick={() => handleNavClick('home')}
            className="flex items-center hover:opacity-90 transition-opacity cursor-pointer"
          >
            <img 
              src="https://assets.auxodev.dk/assets/154be17d-5a98-49b2-8816-0581c018ef0c/eaab3231-8ff7-4e5a-b32b-e264168f88e1.png" 
              alt="Peter Sloth Logo" 
              className="h-7 sm:h-9 md:h-12 w-auto object-contain transition-all duration-300"
            />
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex flex-wrap items-center gap-x-8 gap-y-2 text-sm font-bold tracking-wider md:w-full lg:w-auto md:justify-start lg:justify-end md:mt-4 lg:mt-0 transition-colors duration-300 text-slate-900">
            <button
              onClick={() => handleNavClick('home')}
              className="hover:text-sky-500 transition-colors cursor-pointer uppercase"
            >
              Forside
            </button>
            
            {/* Ydelser Dropdown Container */}
            <div 
              className="relative"
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <button
                onClick={(e) => {
                  e.preventDefault();
                }}
                className="transition-colors cursor-pointer uppercase flex items-center gap-1 py-2"
              >
                <span>Ydelser</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180 text-sky-500' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full w-64 bg-white border border-slate-200 shadow-xl py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45 bg-white border-t border-l border-slate-200" />
                  <div className="relative bg-white z-10">
                    {services.map((service) => (
                      <Link
                        key={service.id}
                        to={`/ydelser/${service.id}`}
                        onClick={() => setIsDropdownOpen(false)}
                        className="block px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-700 hover:text-black hover:bg-slate-50 transition-colors border-l-2 border-transparent hover:border-black text-left"
                      >
                        {service.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/om-os"
              className="hover:text-sky-500 transition-colors cursor-pointer uppercase"
            >
              Om os
            </Link>
            <Link
              to="/galleri"
              className="hover:text-sky-500 transition-colors cursor-pointer uppercase"
            >
              Galleri
            </Link>
            <Link
              to="/kontakt"
              className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 transition-colors rounded-none font-bold cursor-pointer uppercase inline-block text-center"
            >
              Kontakt
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="transition-colors p-1 text-slate-900 hover:text-sky-500"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Navigation */}
      {/* Backdrop Overlay */}
      <div 
        className={`fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-40 md:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Sidebar Panel */}
      <div 
        className={`fixed top-0 right-0 h-full w-[280px] sm:w-[320px] bg-white shadow-2xl z-50 md:hidden flex flex-col transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Sidebar Header */}
        <div className="p-4 border-b border-slate-100 flex justify-between items-center">
          <img 
            src="https://assets.auxodev.dk/assets/154be17d-5a98-49b2-8816-0581c018ef0c/eaab3231-8ff7-4e5a-b32b-e264168f88e1.png" 
            alt="Peter Sloth Logo" 
            className="h-6 w-auto object-contain"
          />
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-1.5 text-slate-500 hover:text-slate-900 transition-colors"
            aria-label="Close menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Sidebar Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4 text-sm font-bold tracking-wider text-slate-900">
          <button
            onClick={() => {
              handleNavClick('home');
              setIsMobileMenuOpen(false);
            }}
            className="w-full text-left py-2 border-b border-slate-100 hover:text-sky-500 transition-colors uppercase"
          >
            Forside
          </button>
          
          {/* Mobile Ydelser Accordion */}
          <div className="border-b border-slate-100">
            <button
              onClick={() => setIsMobileYdelserOpen(!isMobileYdelserOpen)}
              className="w-full flex justify-between items-center py-2 transition-colors uppercase text-left"
            >
              <span>Ydelser</span>
              {isMobileYdelserOpen ? (
                <ChevronUp className="w-4 h-4 text-sky-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-500" />
              )}
            </button>
            
            {isMobileYdelserOpen && (
              <div className="pl-4 pb-3 pt-2 space-y-3 animate-in fade-in slide-in-from-top-2 duration-150">
                {services.map((service) => (
                  <Link
                    key={service.id}
                    to={`/ydelser/${service.id}`}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setIsMobileYdelserOpen(false);
                    }}
                    className="block py-1 text-xs font-bold uppercase tracking-wider text-slate-600 hover:text-sky-500 transition-colors text-left"
                  >
                    {service.title}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/om-os"
            onClick={() => setIsMobileMenuOpen(false)}
            className="block text-left py-2 border-b border-slate-100 hover:text-sky-500 transition-colors uppercase"
          >
            Om os
          </Link>
          <Link
            to="/galleri"
            onClick={() => setIsMobileMenuOpen(false)}
            className="block text-left py-2 border-b border-slate-100 hover:text-sky-500 transition-colors uppercase"
          >
            Galleri
          </Link>
          
          <Link
            to="/kontakt"
            onClick={() => setIsMobileMenuOpen(false)}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 text-center transition-colors font-bold mt-4 uppercase block"
          >
            Kontakt
          </Link>
        </div>

        {/* Sidebar Footer */}
        <div className="p-5 bg-slate-50 border-t border-slate-100 text-xs text-slate-500 space-y-3">
          <div className="font-semibold text-slate-700 uppercase tracking-wider mb-1">Peter Sloth ApS</div>
          <a
            href={`tel:${companyInfo.phoneFormatted}`}
            className="flex items-center gap-2 hover:text-sky-600 transition-colors"
          >
            <Phone className="w-4 h-4 text-slate-400" />
            <span>Tlf: {companyInfo.phone}</span>
          </a>
          <a
            href={`mailto:${companyInfo.email}`}
            className="flex items-center gap-2 hover:text-sky-600 transition-colors"
          >
            <Mail className="w-4 h-4 text-slate-400" />
            <span>{companyInfo.email}</span>
          </a>
          <div className="text-[10px] text-slate-400 pt-2 border-t border-slate-200">
            CVR: {companyInfo.cvr}
          </div>
        </div>
      </div>
    </header>
  );
}
